import React from 'react';
import HighVarianceAlert from './HighVarianceAlert';

const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-center py-1 px-3">
        <p className="text-xs text-gray-600">{label}</p>
        <p className="text-xs font-medium text-gray-900">{value}</p>
    </div>
);

/**
 * A styled container for summary sections with zebra-striped rows.
 * @param {{ title: string, children: React.ReactNode }} props
 */
const Section = ({ title, children }) => {
    // Set the container background to the color of the *next* row, creating a fill effect.
    const containerBg = React.Children.count(children) % 2 === 0 ? 'bg-gray-100' : 'bg-gray-50';

    return (
        <div className="border rounded-lg overflow-hidden flex flex-col">
            <h3 className="px-3 py-2 bg-gray-50 text-xs font-semibold text-gray-700">{title}</h3>
            <div className={`flex-grow ${containerBg}`}>
                <div className="[&>*:nth-child(odd)]:bg-gray-100 [&>*:nth-child(even)]:bg-gray-50">{children}</div>
            </div>
        </div>
    );
};

/**
 * Renders the confirmation view (Step 3) for the pallet prep process, summarizing all user-entered data.
 * @param {{ palletData: object, formData: object, isHighVariance: boolean, varianceDetails: object }} props
 * @returns {JSX.Element}
 */
function PrepStep3({ palletData, formData, isHighVariance, varianceDetails }) {
    const { unitsPerCase } = palletData;
    const {
        numRPCs,
        rpcsPerPallet,
        unitsPerRPC,
        looseUnits,
        scrapUnits,
        scrapReason,
        remainingCases,
        remainingUnits,
        moveToLocationName,
    } = formData;

    // Calculate total units based on form data
    const totalRpcUnits = Number(numRPCs || 0) * Number(unitsPerRPC || 0) + Number(looseUnits || 0);
    const totalRemainingUnits = Number(remainingCases || 0) * Number(unitsPerCase || 0) + Number(remainingUnits || 0);

    return (
        <div className="p-1 max-h-[65vh] overflow-y-auto space-y-4">
            <div className="px-4 py-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-900 font-semibold">Summary</p>
                <p className="text-xs text-green-900">Please review the information before confirming.</p>
            </div>

            {isHighVariance && (
                <HighVarianceAlert
                    variancePercent={varianceDetails.variancePercent}
                    expectedRemainingUnits={varianceDetails.expected}
                    userDeclaredTotal={varianceDetails.userDeclaredTotal}
                />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Section title="RPC Information">
                    <InfoRow label="Number of RPCs:" value={numRPCs} />
                    <InfoRow label="RPCs per pallet:" value={rpcsPerPallet} />
                    <InfoRow label="Units per RPC:" value={unitsPerRPC} />
                    <InfoRow label="Loose Units:" value={looseUnits || 0} />
                    <InfoRow label="Total RPC Units:" value={totalRpcUnits} />
                    <InfoRow label="Scrap Units:" value={scrapUnits || 0} />
                    <InfoRow label="Scrap Reason:" value={Number(scrapUnits) > 0 ? scrapReason : 'N/A'} />
                    <InfoRow label="Location:" value={moveToLocationName} />
                </Section>

                <Section title="Remaining Items">
                    <InfoRow label="Remaining Cases:" value={remainingCases} />
                    <InfoRow label="Units per Case:" value={unitsPerCase} />
                    <InfoRow label="Remaining Loose Units:" value={remainingUnits} />
                    <InfoRow label="Total Remaining Units:" value={totalRemainingUnits} />
                    <InfoRow label="Expected Remaining:" value={varianceDetails.expected} />
                </Section>
            </div>
        </div>
    );
}

export default PrepStep3;
